import React from "react";
import style from "./DebridementCommon.module.scss";
import { ReadOnlyBanner } from "../../../../../secondary/ReadOnlyBanner/ReadOnlyBanner";
import { signedLockedAccess } from "../../../../../../utils";
import { Button, Form, SelectBox } from "../../../../../primary";
import { NOT_PERFORMED_REASONS } from "../debridementConfig";
import { PrerequisiteBlock } from "./PrerequisiteBlock";
import { ProcedureBlock } from "./ProcedureBlock";
import { OutcomeBlock } from "./OutcomeBlock";
import { CANCEL_BTN, SAVE_BTN } from "../../../../../../constants";
import { PERMISSIONS } from "../../../../../../constants/permissions";
// import style from "./debridement.module.scss";

/**
 * Debridement — edit mode.
 *
 * Four stacked blocks rather than one linear chain of questions. Debridement is
 * a tab inside a VerticalTab inside an intervention, so the separation is
 * vertical, not columnar — the horizontal room is already spent.
 *
 * Block 0  Router        Was debridement performed?
 * Block 1  Prerequisites Collapsible. Non-terminal.
 * Block 2  Procedure     What was done.
 * Block 3  Outcome       Captured after.
 *
 * Prop surface is small because the hook exposes a form object rather than
 * ~40 individual flags and setters.
 */

export const DebridementEditMode = ({
  assessmentDetails,
  handleSubmit,
  doSubmit,
  register,
  errors,
  setFormChangesConfirm,
  form,
  careSetting,
  prerequisites,
  flags,
  readiness,
  reasonGaps,
  showReasonErrors,
  performed,
  showApproach,
  showSubcutaneous,
  showPressure,
  readyOpen,
  setReadyOpen,
  setField,
  toggleMulti,
  setPrerequisiteAnswer,
  setPrerequisiteSelection,
  setPrerequisiteReason,
  onCancelClick,
  isReadOnly,
  instrumentsUsedRef,
  necroticTissueRef,
  onMultiSelect,
  control,
}) => (
  <div className={style.cont_spac}>
    {isReadOnly && (
      <div className="ml-2">
        <ReadOnlyBanner />
      </div>
    )}

    {signedLockedAccess(assessmentDetails?.assessmentStage) ? (
      <Form setFormChangesConfirm={setFormChangesConfirm} formChanges onSubmit={handleSubmit(doSubmit)} requiredPermission={PERMISSIONS.TREATMENT}>
        {/* ── Block 0 · router ─────────────────────────────────────── */}
        <section className={style.block}>
          <div className={style.block_head_static}>
            <span className={style.block_num}>0</span>
            <span className={style.block_title}>Was debridement performed?</span>
          </div>
          <div className={style.block_body}>
            <div className={style.field} style={{ maxWidth: "320px" }}>
              <SelectBox
                id="isDebridementPerformed"
                name="isDebridementPerformed"
                optionValue={[
                  { key: "yes", value: "Yes" },
                  { key: "no", value: "No" },
                ]}
                defaultValue={form.isDebridementPerformed || ""}
                disabled={isReadOnly}
                classes={{ inputClassName: style.custom_basic_select }}
                onChange={(e) => setField("isDebridementPerformed", e.target.value)}
              />
            </div>

            {!performed && (
              <div className={style.field2} data-testid="not-performed-reason">
                <label htmlFor="notPerformedReason">Reason not performed</label>
                <SelectBox
                  id="notPerformedReason"
                  name="notPerformedReason"
                  optionValue={NOT_PERFORMED_REASONS}
                  defaultValue={form.notPerformedReason || ""}
                  placeholder="Select…"
                  disabled={isReadOnly}
                  classes={{ inputClassName: style.custom_basic_select, column: style.w50 }}
                  onChange={(e) => setField("notPerformedReason", e.target.value)}
                />
                <p className={style.hint}>
                  The record still saves. A “not performed” entry is a clinical fact, not an empty form.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ── Block 1 · prerequisites ──────────────────────────────── */}
        <PrerequisiteBlock
          prerequisites={prerequisites}
          careSetting={careSetting}
          answers={form.prerequisiteAnswers}
          readiness={readiness}
          flagCount={flags.length}
          open={readyOpen}
          setReadyOpen={setReadyOpen}
          onAnswer={setPrerequisiteAnswer}
          onSelection={setPrerequisiteSelection}
          onReason={setPrerequisiteReason}
          reasonGaps={reasonGaps}
          showReasonErrors={showReasonErrors}
          disabled={isReadOnly || !performed}
          register={register}
          errors={errors}
          control={control}
        />

        {/* ── Block 2 · procedure ──────────────────────────────────── */}
        <ProcedureBlock
          form={form}
          showApproach={showApproach}
          showSubcutaneous={showSubcutaneous}
          disabled={isReadOnly || !performed}
          onField={setField}
          onToggleMulti={toggleMulti}
          register={register}
          errors={errors}
          necroticTissueRef={necroticTissueRef}
          instrumentsUsedRef={instrumentsUsedRef}
          onMultiSelect={onMultiSelect}
        />

        {/* ── Block 3 · outcome ────────────────────────────────────── */}
        <OutcomeBlock
          form={form}
          showPressure={showPressure}
          disabled={isReadOnly || !performed}
          onField={setField}
        />

        {/* ── flags ────────────────────────────────────────────────── */}
        {flags.length > 0 ? (
          <div className={style.flags} data-testid="compliance-flags">
            <div className={style.flags_head}>
              {flags.length} compliance flag{flags.length > 1 ? "s" : ""} — the record still saves
            </div>
            {flags.map((f) => (
              <div className={style.flags_row} key={f.prerequisiteKey}>
                <strong>{f.label}</strong>
                <span>{f.reasonText || f.reasonCode || "No reason given"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className={style.flags_ok}>
            No compliance gaps. All prerequisites shown for this care setting are satisfied.
          </div>
        )}

        {/* ── actions ──────────────────────────────────────────────── */}
        { (
          <div className={style.action_submit}>
            <Button
              type="button"
              id="cancel"
              value={CANCEL_BTN}
              classes={{ className: `btn ${style.btn_cancel}` }}
              onClick={onCancelClick}
            />
            {/*
              Save is NEVER disabled by flags.length. A compliance gap annotates
              a saved record; it does not prevent one. Gating this button
              recreates the V1 defect where an already-performed procedure
              could not be recorded at all.
            */}
            <Button type="submit" id="updateBtn" value={SAVE_BTN} classes={{ className: `btn ${style.btn_save}` }} />
          </div>
        )}
      </Form>
    ) : (
      "This wound assessment is signed and locked, cannot be updated."
    )}
  </div>
);
